import { createContext, useContext, ReactNode, useState, useEffect, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { getMemberSession } from "../utils/memberSession";
import { directoryEmailFor } from "../utils/directoryIdentity";
import { authFetch, authGetSwr } from "../utils/authFetch";

type Role = "admin" | "content_admin" | "employee";

interface RoleContextType {
  role: Role;
  isAdmin: boolean;
  /** Nur Modulbearbeitung (features.content_admins), kein übriger Admin-Bereich. Bei Admins false. */
  isContentAdmin: boolean;
  tenantId: string;
  isLoading: boolean;
}

const RoleContext = createContext<RoleContextType>({ role: "employee", isAdmin: false, isContentAdmin: false, tenantId: "", isLoading: true });

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

function normalizeTenantId(value: string): string {
  return value.trim().toLowerCase();
}

function normalizeStoragePrefix(value: string): string {
  return value.trim();
}

function normalizeStorageTenantId(value: string): string {
  return value.trim().toLowerCase();
}

export function getTenantStorageKey(prefix: string, tenantId: string): string | null {
  const normalizedPrefix = normalizeStoragePrefix(prefix);
  const normalizedTenantId = normalizeStorageTenantId(tenantId);
  if (!normalizedPrefix || !normalizedTenantId || normalizedTenantId === "default") return null;
  return `${normalizedPrefix}_${normalizedTenantId}`;
}

function parseRoleClaims(rawRoles: unknown): string[] {
  if (!Array.isArray(rawRoles)) return [];
  return rawRoles
    .filter((role): role is string => typeof role === "string")
    .map((role) => role.trim().toLowerCase())
    .filter(Boolean);
}

function parseAdminAllowlist(rawAllowlist: string | undefined): Array<{ tenantId: string; email: string }> {
  if (!rawAllowlist) return [];
  return rawAllowlist.split(",").reduce<Array<{ tenantId: string; email: string }>>((entries, chunk) => {
    const [rawTenant, rawEmail] = chunk.split(":");
    if (!rawTenant || !rawEmail) return entries;

    const tenantId = normalizeTenantId(rawTenant);
    const email = normalizeEmail(rawEmail);
    if (!tenantId || !email) return entries;

    entries.push({ tenantId, email });
    return entries;
  }, []);
}


// Ob jemand Admin ist, steht erst nach der Antwort aus der DB sicher fest. Bis
// dahin zeigte die App nur den Spinner — beim ersten Oeffnen des Tages sind das
// leicht mehrere Sekunden, weil die Vercel-Function dann kalt ist. Stattdessen
// merken wir uns das Ergebnis des letzten Starts pro Konto und rendern sofort
// damit. Weicht die echte Antwort ab (Rolle vergeben oder entzogen), wird der
// Merker korrigiert und die Seite einmal neu geladen — dann stimmen auch die
// registrierten Routen. Beim naechsten Start passt der Merker, es gibt also
// keine zweite Runde.
const ADMIN_HINT_PREFIX = "sherloxaware_admin_hint_v1:";

// "1" = Admin, "c" = Content-Admin, "0" = Mitarbeiter.
function readAdminHint(email: string): Role | null {
  if (!email) return null;
  try {
    const raw = localStorage.getItem(ADMIN_HINT_PREFIX + email);
    return raw === "1" ? "admin" : raw === "c" ? "content_admin" : raw === "0" ? "employee" : null;
  } catch {
    return null;
  }
}

function writeAdminHint(email: string, role: Role): void {
  const raw = role === "admin" ? "1" : role === "content_admin" ? "c" : "0";
  try { localStorage.setItem(ADMIN_HINT_PREFIX + email, raw); } catch { /* ignore */ }
}

// Vor dem ersten Redirect festhalten: nach einem Neuladen wegen geaenderter
// Rolle soll die urspruenglich geoeffnete Adresse wieder erscheinen.
const INITIAL_PATH = typeof window === "undefined"
  ? "/"
  : window.location.pathname + window.location.search;

// Frische Claims aus Entra holen wir nur noch im Hintergrund und hoechstens
// einmal pro Stunde. Grund: acquireTokenSilent teilt sich intern eine
// Warteschlange pro Konto+Scope, und `forceRefresh` gehoert NICHT zum
// Schluessel (StandardController.acquireTokenSilent). Ein erzwungener Refresh
// beim Start haengt sich also vor jeden Token-Abruf aus authFetch — faellt die
// stille Erneuerung auf den Iframe zurueck, warten daran auch saemtliche
// API-Aufrufe. App-Rollen aendern sich selten; die eigentliche Admin-Quelle ist
// ohnehin die Liste aus der DB, die bei jedem Start frisch geholt wird.
const CLAIMS_REFRESH_PREFIX = "sherloxaware_claims_refreshed_at_v1:";
const CLAIMS_REFRESH_INTERVAL_MS = 60 * 60_000;

function claimsRefreshDue(accountKey: string): boolean {
  if (!accountKey) return false;
  try {
    const raw = localStorage.getItem(CLAIMS_REFRESH_PREFIX + accountKey);
    if (!raw) return true;
    const ts = Number(raw);
    return !Number.isFinite(ts) || Date.now() - ts > CLAIMS_REFRESH_INTERVAL_MS;
  } catch {
    return false;
  }
}

function markClaimsRefreshed(accountKey: string): void {
  try { localStorage.setItem(CLAIMS_REFRESH_PREFIX + accountKey, String(Date.now())); } catch { /* ignore */ }
}

function afterFirstPaint(run: () => void): () => void {
  const w = window as Window & { requestIdleCallback?: (fn: () => void, opts?: { timeout: number }) => number };
  if (typeof w.requestIdleCallback === "function") {
    const id = w.requestIdleCallback(run, { timeout: 5000 });
    return () => { (window as unknown as { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback?.(id); };
  }
  const id = window.setTimeout(run, 1500);
  return () => window.clearTimeout(id);
}

export function RoleProvider({ children }: { children: ReactNode }) {
  const { instance, accounts } = useMsal();
  const account = instance.getActiveAccount() ?? accounts[0];
  const rawTenantId = account?.tenantId ?? "";
  const tenantId = rawTenantId.trim();
  const normalizedTenantId = normalizeTenantId(tenantId);
  // Anmeldename aus Entra und die Adresse, unter der das Verzeichnis das Konto
  // führt (meist identisch, siehe utils/directoryIdentity.ts). Admin-Listen
  // wurden von Hand gepflegt und können beide Schreibweisen enthalten.
  const loginEmail = normalizeEmail(account?.username ?? "");
  const email = directoryEmailFor(loginEmail);
  const ownEmails = email === loginEmail ? [email] : [email, loginEmail];

  // Cached claims from MSAL localStorage — may be stale after new App Role assignment
  const cachedClaims = account?.idTokenClaims as Record<string, unknown> | undefined;
  const [freshClaims, setFreshClaims] = useState<Record<string, unknown> | undefined>(undefined);

  // Admin-Liste des Mandanten aus Supabase (tenant_entitlements.admins[]).
  // Zentral pflegbar im GA-Portal; beim ersten Login wird der erste Nutzer
  // automatisch als Admin provisioniert. Dritte Admin-Quelle neben Azure-Rolle + Env.
  const [dbAdmins, setDbAdmins] = useState<string[]>([]);
  const [dbContentAdmins, setDbContentAdmins] = useState<string[]>([]);

  // Both admin-detection sources (fresh Azure claims + DB allowlist) are async;
  // isAdmin must not be trusted for routing decisions until both have settled at least once.
  // Die Claims aus dem MSAL-Cache liegen synchron vor — damit ist diese Quelle
  // beim Start sofort beantwortet, sofern ueberhaupt etwas im Cache steht.
  const [claimsResolved, setClaimsResolved] = useState(() => cachedClaims !== undefined);
  const [dbAdminsResolved, setDbAdminsResolved] = useState(false);
  // Nur wenn die Admin-Liste wirklich ankam, darf sie den Merker ueberschreiben.
  const [dbAdminsOk, setDbAdminsOk] = useState(false);

  const accountKey = account?.homeAccountId ?? "";

  useEffect(() => {
    if (!account) {
      setClaimsResolved(true);
      return;
    }
    // Neu vergebene App-Rollen stehen nur in einem frischen Token. Ohne Claims im
    // Cache brauchen wir das sofort, sonst laeuft es gedrosselt im Hintergrund
    // (siehe CLAIMS_REFRESH_INTERVAL_MS) und haelt den Start nicht mehr auf.
    const refresh = () => {
      instance
        .acquireTokenSilent({ scopes: ["User.Read"], account, forceRefresh: true })
        .then((response) => {
          const decoded = response.idTokenClaims as Record<string, unknown> | undefined;
          markClaimsRefreshed(accountKey);
          if (decoded) setFreshClaims(decoded);
        })
        .catch(() => {
          // Bleibt beim Stand aus dem MSAL-Cache. Ist die Sitzung wirklich
          // abgelaufen, loest der naechste authFetch den Redirect aus.
        })
        .finally(() => setClaimsResolved(true));
    };

    if (cachedClaims === undefined) {
      refresh();
      return;
    }
    setClaimsResolved(true);
    if (!claimsRefreshDue(accountKey)) return;
    return afterFirstPaint(refresh);
  // Re-run when the active account changes (i.e. after login/logout)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.homeAccountId]);

  // Sync user profile to Supabase on login (name also synced every 3min via Layout.tsx)
  const accountName = account?.name ?? "";
  useEffect(() => {
    if (!email || !tenantId || !accountName) return;
    // Sammelaccount: das Profil der angemeldeten Person pflegt der Server beim
    // Sub-Login. Hier würde sonst der Name des gemeinsamen Kontos den selbst
    // gesetzten Namen des Mitarbeiters überschreiben.
    if (getMemberSession()) return;
    authFetch("/api/user-profiles", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tenantId, email, displayName: accountName }),
    }).catch(() => {});
  }, [email, tenantId, accountName]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!email || !tenantId) {
      setDbAdminsResolved(true);
      return;
    }
    // Liegt die Antwort vom letzten Start im Cache, kommt sie sofort; die frische
    // wird nachgereicht, sobald sie da ist und sich unterscheidet.
    const cancel = authGetSwr(`/api/entitlements?tenantId=${encodeURIComponent(tenantId)}`, (data) => {
      const list =
        data && typeof data === "object" && Array.isArray((data as { admins?: unknown }).admins)
          ? (data as { admins: unknown[] }).admins
          : [];
      setDbAdmins(
        list.filter((entry): entry is string => typeof entry === "string").map((entry) => entry.toLowerCase().trim()),
      );
      const features = data && typeof data === "object" ? (data as { features?: { content_admins?: unknown } }).features : undefined;
      const contentList = Array.isArray(features?.content_admins) ? features.content_admins : [];
      setDbContentAdmins(
        contentList.filter((entry): entry is string => typeof entry === "string").map((entry) => entry.toLowerCase().trim()),
      );
      setDbAdminsOk(true);
      setDbAdminsResolved(true);
    });
    // Antwortet niemand (offline, Cold Start laeuft in einen Fehler), darf der
    // Start trotzdem nicht haengen bleiben.
    const giveUp = window.setTimeout(() => setDbAdminsResolved(true), 8000);
    return () => { cancel(); window.clearTimeout(giveUp); };
  }, [email, tenantId]);

  const claims = freshClaims ?? cachedClaims;
  const azureRoles = parseRoleClaims(claims?.roles);
  const hasAzureAdminRole = azureRoles.includes("admin");

  const allowlistedAdmins = parseAdminAllowlist(import.meta.env.VITE_BOOTSTRAP_ADMIN_EMAILS);
  const hasAllowlistAdminAccess =
    email !== "" &&
    allowlistedAdmins.some((entry) => entry.tenantId === normalizedTenantId && ownEmails.includes(entry.email));

  const hasDbAdminAccess = email !== "" && dbAdmins.some((entry) => ownEmails.includes(entry));

  // Wer über einen Sammelaccount angemeldet ist, erbt keine Adminrechte des
  // gemeinsamen Kontos – der Server sieht das genauso (isAdmin in api/_auth.js).
  const isSharedMemberSession = getMemberSession() !== null;
  const resolvedIsAdmin = hasAzureAdminRole || hasAllowlistAdminAccess || hasDbAdminAccess;
  const resolvedRole: Role = resolvedIsAdmin
    ? "admin"
    : email !== "" && dbContentAdmins.some((entry) => ownEmails.includes(entry)) ? "content_admin" : "employee";

  // Merker vom letzten Start dieses Kontos (siehe ADMIN_HINT_PREFIX).
  const adminHint = useMemo(
    () => (isSharedMemberSession ? null : readAdminHint(email)),
    [email, isSharedMemberSession],
  );

  const settled = claimsResolved && dbAdminsResolved;
  // Kam die Admin-Liste nicht an, bleibt der Merker gueltig — sonst verloere ein
  // Admin bei einem einzelnen Netzwerkfehler stumm seine Oberflaeche.
  const trusted = settled && dbAdminsOk;
  const effectiveRole: Role = isSharedMemberSession ? "employee" : (trusted ? resolvedRole : (adminHint ?? resolvedRole));
  const isAdmin = effectiveRole === "admin";
  const isContentAdmin = effectiveRole === "content_admin";
  // Blockiert nur noch beim allerersten Start auf diesem Geraet, wenn es keinen
  // Merker gibt und die Routen sonst fuer die falsche Rolle registriert wuerden.
  const isLoading = !settled && adminHint === null && !isSharedMemberSession;

  useEffect(() => {
    if (!trusted || !email || isSharedMemberSession) return;
    const previous = readAdminHint(email);
    writeAdminHint(email, resolvedRole);
    // Der Merker lag daneben: die Routen wurden fuer die falsche Rolle
    // registriert. Einmal neu laden — der Merker stimmt jetzt, also gibt es
    // keine Schleife.
    if (previous !== null && previous !== resolvedRole) {
      window.location.replace(INITIAL_PATH);
    }
  }, [trusted, email, resolvedRole, isSharedMemberSession]);

  if (import.meta.env.DEV) {
    console.log("[SHERLOXAWARE] isAdmin:", isAdmin, "| isContentAdmin:", isContentAdmin, "| isLoading:", isLoading, "| azureRoles:", azureRoles, "| allowlist:", hasAllowlistAdminAccess, "| dbAdmins:", hasDbAdminAccess, "| hint:", adminHint, "| email:", email, "| tenantId:", normalizedTenantId);
  }
  const contextValue = useMemo(
    () => ({ role: effectiveRole, isAdmin, isContentAdmin, tenantId, isLoading }),
    [effectiveRole, isAdmin, isContentAdmin, tenantId, isLoading],
  );

  return (
    <RoleContext.Provider value={contextValue}>
      {children}
    </RoleContext.Provider>
  );
}

export function useRole() {
  return useContext(RoleContext);
}
